import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { guestApi } from "../api/guestApi";
import type {
  CreateGuestReleaseData,
  GuestPaginationParams,
  UpdateGuestReleaseData,
} from "../types";

export const useGuestReleases = (params?: GuestPaginationParams) => {
  return useQuery({
    queryKey: ["guest-releases", params],
    queryFn: () => guestApi.getReleases(params),
  });
};

export const useGuestRelease = (id: string) => {
  return useQuery({
    queryKey: ["guest-release", id],
    queryFn: () => guestApi.getRelease(id),
    enabled: !!id,
  });
};

export const useGuestStats = () => {
  return useQuery({
    queryKey: ["guest-stats"],
    queryFn: () => guestApi.getStats(),
  });
};

export const useCreateGuestRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGuestReleaseData) => guestApi.createRelease(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-releases"] });
      queryClient.invalidateQueries({ queryKey: ["guest-stats"] });
    },
  });
};

export const useCreateGuestReleaseWithProgress = () => {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    mutationFn: (data: CreateGuestReleaseData) =>
      guestApi.createRelease(data, setUploadProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-releases"] });
      queryClient.invalidateQueries({ queryKey: ["guest-stats"] });
    },
    onSettled: () => {
      setUploadProgress(0);
    },
  });

  return {
    ...mutation,
    uploadProgress,
    resetProgress: () => setUploadProgress(0),
  };
};

export const useUpdateGuestRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGuestReleaseData }) =>
      guestApi.updateRelease(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-releases"] });
      queryClient.invalidateQueries({ queryKey: ["guest-stats"] });
    },
  });
};

export const useDeleteGuestRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guestApi.deleteRelease(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-releases"] });
      queryClient.invalidateQueries({ queryKey: ["guest-stats"] });
    },
  });
};

export const usePublishGuestRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guestApi.publishRelease(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-releases"] });
      queryClient.invalidateQueries({ queryKey: ["guest-stats"] });
    },
  });
};

export const useArchiveGuestRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guestApi.archiveRelease(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guest-releases"] });
      queryClient.invalidateQueries({ queryKey: ["guest-stats"] });
    },
  });
};
